import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, Dialog, DialogBody, DialogFooter, DialogHeader, Spinner } from '@material-tailwind/react';
import { AlertTriangleIcon, GaugeIcon, SaveIcon, BuildingIcon } from 'lucide-react';
import {
    useGetMeetingRoomQuotaDefaults,
    useUpdateMeetingRoomQuotaDefaults,
} from '../../../custom-hooks/use-settings';
import { useAuth } from '../../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../../utils/enums';
import { GetMeetingRooms } from '../../../services/backend';

const SettingsMeetingRoom = () => {
    const { userDetails } = useAuth();
    const { data, isLoading } = useGetMeetingRoomQuotaDefaults();
    const updateDefaultsMutation = useUpdateMeetingRoomQuotaDefaults();

    const [monthlyHours, setMonthlyHours] = useState(8);
    const [meetingRoomHoursPerDay, setMeetingRoomHoursPerDay] = useState(2);
    const [roomQuotas, setRoomQuotas] = useState([]);
    
    const [meetingRooms, setMeetingRooms] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(false);

    const [applyToExisting, setApplyToExisting] = useState(false);
    const [keepCustomUsers, setKeepCustomUsers] = useState(false);
    const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [targetMonthly, setTargetMonthly] = useState('');
    const [targetDaily, setTargetDaily] = useState('');

    const defaults = data?.defaults || {};

    useEffect(() => {
        const fetchRooms = async () => {
            try {
                setLoadingRooms(true);
                const res = await GetMeetingRooms();
                setMeetingRooms(res.data.meetingRooms || []);
            } catch (e) {
                console.error('Failed to fetch meeting rooms:', e);
            } finally {
                setLoadingRooms(false);
            }
        };
        fetchRooms();
    }, []);

    useEffect(() => {
        if (typeof defaults.monthlyHours === 'number') {
            setMonthlyHours(defaults.monthlyHours);
            if (!targetMonthly) setTargetMonthly(defaults.monthlyHours);
        }
        if (typeof defaults.meetingRoomHoursPerDay === 'number') {
            setMeetingRoomHoursPerDay(defaults.meetingRoomHoursPerDay);
            if (!targetDaily) setTargetDaily(defaults.meetingRoomHoursPerDay);
        }
        if (Array.isArray(defaults.roomQuotas)) {
            setRoomQuotas(defaults.roomQuotas.map(rq => ({
                room: rq.room?._id || rq.room,
                monthlyHours: rq.monthlyHours || 0,
                dailyHours: rq.dailyHours || 0,
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaults.monthlyHours, defaults.meetingRoomHoursPerDay, defaults.roomQuotas]);

    const isSaving = updateDefaultsMutation.isLoading;
    
    const getRoomQuota = (roomId) => {
        return roomQuotas.find(rq => rq.room?.toString() === roomId?.toString()) || { room: roomId, monthlyHours: 0, dailyHours: 0 };
    };

    const updateRoomQuota = (roomId, field, value) => {
        const parsed = parseFloat(value) || 0;
        setRoomQuotas(prev => {
            let nextQuotas = [];
            const existing = prev.find(rq => rq.room?.toString() === roomId?.toString());
            if (existing) {
                nextQuotas = prev.map(rq => rq.room?.toString() === roomId?.toString() ? { ...rq, [field]: parsed } : rq);
            } else {
                nextQuotas = [...prev, { room: roomId, monthlyHours: 0, dailyHours: 0, [field]: parsed }];
            }

            if (field === 'monthlyHours') {
                const totalMonthly = nextQuotas.reduce((acc, curr) => acc + (parseFloat(curr.monthlyHours) || 0), 0);
                setMonthlyHours(totalMonthly);
            }

            return nextQuotas;
        });
    };

    const proceedToFinalConfirmation = async () => {
        const filteredRoomQuotas = roomQuotas.filter(rq => rq.monthlyHours > 0 || rq.dailyHours > 0);
        if (!applyToExisting) {
            await updateDefaultsMutation.mutateAsync({
                monthlyHours: Number(monthlyHours),
                meetingRoomHoursPerDay: Number(meetingRoomHoursPerDay),
                roomQuotas: filteredRoomQuotas,
                applyToExistingUsers: false,
                keepCustomUsers: false,
            });
            setIsApplyModalOpen(false);
        } else {
            setIsApplyModalOpen(false);
            setIsConfirmModalOpen(true);
        }
    };

    const handleSaveAndApply = async () => {
        const filteredRoomQuotas = roomQuotas.filter(rq => rq.monthlyHours > 0 || rq.dailyHours > 0);
        await updateDefaultsMutation.mutateAsync({
            monthlyHours: Number(monthlyHours),
            meetingRoomHoursPerDay: Number(meetingRoomHoursPerDay),
            roomQuotas: filteredRoomQuotas,
            applyToExistingUsers: true,
            keepCustomUsers,
            targetPreviousMonthlyHours: targetMonthly ? Number(targetMonthly) : undefined,
            targetPreviousDailyHours: targetDaily ? Number(targetDaily) : undefined,
        });
        setIsConfirmModalOpen(false);
        setConfirmationText('');
    };

    const openApplyFlow = () => {
        setIsApplyModalOpen(true);
    };

    if (isLoading) {
        return (
            <div className='min-h-[60vh] flex items-center justify-center'>
                <Spinner className='h-8 w-8' />
            </div>
        );
    }

    const hasAccess = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                      userDetails?.role === STATIC_USER_ROLES.ADMIN ||
                      (userDetails?.role === STATIC_USER_ROLES.STAFF && userDetails?.dynamicRole?.permissions?.includes('FX22-1:manage'));

    if (!hasAccess) {
        return (
            <div className='flex min-h-[60vh] flex-col items-center justify-center p-8'>
                <div className='rounded-full bg-blue-gray-50 p-6'>
                    <AlertTriangleIcon size={48} className='text-blue-gray-300' />
                </div>
                <h2 className='mt-6 text-xl font-semibold text-blue-gray-800'>No Settings Available</h2>
                <p className='mt-2 text-center text-sm text-blue-gray-500'>
                    Your current role does not have access to this settings page.
                </p>
            </div>
        );
    }

    return (
        <div className='p-4 md:p-8'>
            <div className='w-full space-y-6'>
                <div className='relative overflow-hidden rounded-2xl border border-blue-gray-100 bg-gradient-to-br from-zinc-50 via-white to-zinc-100 p-6'>
                    <div className='absolute -top-14 -right-8 h-40 w-40 rounded-full bg-zinc-200/50 blur-2xl' />
                    <div className='relative'>
                        <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-500'>System Policy</p>
                        <h1 className='mt-1 text-2xl font-semibold text-blue-gray-900'>Meeting Room Quota Defaults</h1>
                        <p className='mt-2 text-sm text-blue-gray-600'>Manage the default booking limits for your workspaces.</p>
                    </div>
                </div>

                <Card className='border border-blue-gray-100 shadow-sm'>
                    <CardBody className='space-y-6'>
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                            <div className='rounded-xl border border-blue-gray-100 p-4'>
                                <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                    <GaugeIcon size={16} />
                                    <p className='text-sm font-semibold'>Default Monthly Hours</p>
                                </div>
                                <input
                                    type='number'
                                    min={1}
                                    max={30}
                                    step={1}
                                    value={monthlyHours}
                                    readOnly
                                    className='w-full rounded-lg border border-blue-gray-200 bg-gray-100 text-gray-500 px-3 py-2 text-sm outline-none transition cursor-not-allowed'
                                    disabled={true}
                                />
                                <p className='mt-2 text-xs text-blue-gray-500'>Applies to newly created users by default. You can choose to update existing users upon saving.</p>
                            </div>

                            <div className='rounded-xl border border-blue-gray-100 p-4'>
                                <div className='mb-3 flex items-center gap-2 text-blue-gray-700'>
                                    <GaugeIcon size={16} />
                                    <p className='text-sm font-semibold'>Default Daily Limit (Hours)</p>
                                </div>
                                <input
                                    type='range'
                                    min={1}
                                    max={10}
                                    step={1}
                                    value={meetingRoomHoursPerDay}
                                    onChange={(e) => setMeetingRoomHoursPerDay(e.target.value)}
                                    className='w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-0 accent-black'
                                    style={{ background: `linear-gradient(to right, black ${((meetingRoomHoursPerDay - 1) / 9) * 100}%, #e2e8f0 ${((meetingRoomHoursPerDay - 1) / 9) * 100}%)` }}
                                    disabled={isSaving}
                                />
                                <p className='mt-2 text-sm font-semibold text-blue-gray-700'>{meetingRoomHoursPerDay} hours/day</p>
                            </div>
                        </div>

                        <div className="mt-6 border-t pt-6">
                            <div className="flex items-center gap-2 mb-3">
                                <BuildingIcon size={14} className="text-gray-500" />
                                <p className="text-sm font-semibold text-gray-700">Per-Room Default Sub-Limits</p>
                                <span className="text-xs text-gray-400">(0 = no room-specific cap)</span>
                            </div>

                            {loadingRooms ? (
                                <div className="flex justify-center py-4"><Spinner className="h-5 w-5" /></div>
                            ) : meetingRooms.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">No meeting rooms found.</p>
                            ) : (
                                <div className="space-y-3">
                                    {meetingRooms.map(room => {
                                        const rq = getRoomQuota(room._id);
                                        return (
                                            <div key={room._id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: room.idColor || '#ccc' }} />
                                                    <p className="text-sm font-medium text-gray-700 truncate">{room.code} — {room.number}</p>
                                                </div>
                                                <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto mt-2 sm:mt-0">
                                                    <div className="flex flex-col flex-1 sm:flex-none items-start sm:items-center gap-1">
                                                        <label className="text-xs text-gray-500 font-medium">Monthly (hrs)</label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={0.5}
                                                            className="w-full sm:w-20 text-center border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                            value={rq.monthlyHours}
                                                            onChange={e => updateRoomQuota(room._id, 'monthlyHours', e.target.value)}
                                                            disabled={isSaving}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col flex-1 sm:flex-none items-start sm:items-center gap-1">
                                                        <label className="text-xs text-gray-500 font-medium">Daily (hrs)</label>
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step={0.5}
                                                            className="w-full sm:w-20 text-center border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
                                                            value={rq.dailyHours}
                                                            onChange={e => updateRoomQuota(room._id, 'dailyHours', e.target.value)}
                                                            disabled={isSaving}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="text-xs text-gray-500 mt-3">
                                Enter limits in <strong>hours</strong> (e.g. 1.5). Rooms with 0 in both fields will solely use the global default pool.
                            </p>
                        </div>

                        <div className='flex flex-col gap-3 md:flex-row mt-6 pt-6 border-t'>
                            <Button
                                className='min-w-[160px] rounded-full flex items-center justify-center gap-2 bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60'
                                onClick={openApplyFlow}
                                disabled={isSaving}
                            >
                                <SaveIcon size={16} />
                                Save and Apply
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Dialog open={isApplyModalOpen} handler={() => setIsApplyModalOpen(false)} size='md' className='rounded-2xl border-none outline-none'>
                <DialogHeader className='flex items-center gap-2'>
                    <AlertTriangleIcon size={18} className='text-amber-700' />
                    Save Limits
                </DialogHeader>
                <DialogBody className='space-y-4 text-blue-gray-700 max-h-[60vh] overflow-y-auto'>
                    <p className='text-sm'>
                        You are about to save the new workspace defaults.
                    </p>
                    <p className='text-sm font-medium text-red-700'>
                        This operation is irreversible from this flow. Please review carefully before continuing.
                    </p>

                    <div className='rounded-xl border border-blue-gray-100 bg-zinc-50 p-4'>
                        <p className='text-[11px] font-semibold uppercase tracking-wider text-blue-gray-500'>New Limits to Apply</p>
                        <div className='mt-2 flex items-center gap-6'>
                            <div>
                                <p className='text-[10px] text-blue-gray-500'>Monthly Hours</p>
                                <p className='text-lg font-bold text-black'>{monthlyHours}</p>
                            </div>
                            <div className='h-8 w-px bg-blue-gray-200' />
                            <div>
                                <p className='text-[10px] text-blue-gray-500'>Daily Hours</p>
                                <p className='text-lg font-bold text-black'>{meetingRoomHoursPerDay}</p>
                            </div>
                        </div>
                        
                        {roomQuotas.filter(rq => rq.monthlyHours > 0 || rq.dailyHours > 0).length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className='text-[10px] text-blue-gray-500 mb-2'>Per-Room Caps</p>
                                <div className="space-y-1">
                                    {roomQuotas.filter(rq => rq.monthlyHours > 0 || rq.dailyHours > 0).map(rq => {
                                        const room = meetingRooms.find(r => r._id === rq.room);
                                        return (
                                            <div key={rq.room} className="flex justify-between items-center text-xs">
                                                <span className="font-medium text-gray-700">{room ? `${room.code} - ${room.number}` : 'Unknown Room'}</span>
                                                <span className="text-gray-600 font-semibold">{rq.monthlyHours}h/mo, {rq.dailyHours}h/day</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='rounded-xl border border-blue-gray-100 bg-blue-gray-50/40 p-4'>
                        <label className='flex cursor-pointer items-start gap-3'>
                            <input
                                type='checkbox'
                                className='mt-1 h-4 w-4 rounded border-blue-gray-300 focus:outline-none focus:ring-0'
                                checked={applyToExisting}
                                onChange={(e) => setApplyToExisting(e.target.checked)}
                                disabled={isSaving}
                            />
                            <span>
                                <span className='block text-sm font-semibold text-blue-gray-800'>Apply defaults to existing customers</span>
                                <span className='block text-xs text-blue-gray-600'>
                                    Check this to push these new limits (global and per-room) to users already using the system.
                                </span>
                            </span>
                        </label>

                        {applyToExisting && (
                            <div className='mt-4 ml-7 rounded-xl border border-blue-gray-200 bg-white p-4 shadow-sm'>
                                <label className='flex cursor-pointer items-start gap-3'>
                                    <input
                                        type='checkbox'
                                        className='mt-1 h-4 w-4 rounded border-blue-gray-300 focus:outline-none focus:ring-0'
                                        checked={keepCustomUsers}
                                        onChange={(e) => setKeepCustomUsers(e.target.checked)}
                                        disabled={isSaving}
                                    />
                                    <span>
                                        <span className='block text-sm font-semibold text-blue-gray-800'>Keep custom users unchanged</span>
                                        <span className='block text-xs text-blue-gray-600'>
                                            When enabled, only users matching the target quota below will be updated.
                                        </span>
                                    </span>
                                </label>
                                {keepCustomUsers && (
                                    <div className='mt-4 flex flex-col gap-3 rounded-lg border border-blue-gray-200 bg-zinc-50 p-3'>
                                        <p className='text-xs font-semibold text-blue-gray-500'>TARGET USERS CURRENTLY HAVING:</p>
                                        <div className='flex gap-4'>
                                            <div className='flex-1'>
                                                <label className='mb-1 block text-xs text-blue-gray-700'>Monthly Hours</label>
                                                <input
                                                    type='number'
                                                    className='w-full rounded border border-blue-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-gray-500'
                                                    value={targetMonthly}
                                                    onChange={(e) => setTargetMonthly(e.target.value)}
                                                />
                                            </div>
                                            <div className='flex-1'>
                                                <label className='mb-1 block text-xs text-blue-gray-700'>Daily Hours</label>
                                                <input
                                                    type='number'
                                                    className='w-full rounded border border-blue-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-gray-500'
                                                    value={targetDaily}
                                                    onChange={(e) => setTargetDaily(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <p className='text-[10px] text-blue-gray-400'>
                                            If you recently saved a new default, make sure these match the OLD default you want to replace!
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className='gap-2'>
                    <Button variant='text' color='blue-gray' className='focus:outline-none focus:ring-0' onClick={() => setIsApplyModalOpen(false)} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button className='min-w-[120px] flex justify-center bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60' onClick={proceedToFinalConfirmation} disabled={isSaving}>
                        {isSaving ? <Spinner className='h-4 w-4' /> : 'Continue'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog open={isConfirmModalOpen} handler={() => setIsConfirmModalOpen(false)} size='sm' className='rounded-2xl border-none outline-none'>
                <DialogHeader>Final Confirmation</DialogHeader>
                <DialogBody className='space-y-4'>
                    <p className='text-sm text-blue-gray-700'>
                        Type <span className='font-semibold'>CONFIRM</span> to apply default quota to existing users.
                    </p>
                    <input
                        type='text'
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        className='w-full rounded-lg border border-blue-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-gray-700 focus:outline-none focus:ring-0'
                        placeholder='Type CONFIRM'
                        disabled={isSaving}
                    />
                </DialogBody>
                <DialogFooter className='gap-2'>
                    <Button
                        variant='text'
                        color='blue-gray'
                        className='focus:outline-none focus:ring-0'
                        onClick={() => {
                            setIsConfirmModalOpen(false);
                            setConfirmationText('');
                        }}
                        disabled={isSaving}
                    >
                        Back
                    </Button>
                    <Button
                        className='bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60'
                        onClick={handleSaveAndApply}
                        disabled={isSaving || confirmationText.trim() !== 'CONFIRM'}
                    >
                        {isSaving ? <Spinner className='h-4 w-4' /> : 'Confirm Apply'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default SettingsMeetingRoom;
